import { useCometMcpServers } from '#pplx/frontend/apps/ask/comet-browser/hooks/useCometMcpServers.js';
import { createSourceAllowedPredicate, createSourceConnectedPredicate, createSourceLabelGetter, getSourceIds, } from '#pplx/frontend/apps/ask/sources/hooks/sourceProjection.js';
import { ASK_EXEC_ENVS } from '#pplx/frontend/apps/ask/sources/hooks/sourceStatusPredicates.js';
import { useApiSources } from '#pplx/frontend/apps/ask/sources/hooks/useApiSources.js';
import { useSourceLabel } from '#pplx/frontend/apps/ask/sources/hooks/useSourceLabel.js';
import { getCometMcpServerName } from '#pplx/frontend/apps/ask/sources/types/cometMcpSourceTypes.js';
import { isSourceOmitted } from '#pplx/frontend/apps/ask/sources/types/omittedSources.js';
import { EMPTY_ARRAY } from '#pplx/frontend/common/defaults.js';
import { useCallback, useMemo } from 'react';
export const useSources = ({ enabled = true, omittedSources = EMPTY_ARRAY, omitCometMcpSources = false, noLimit, searchQuery, productSurface, }) => {
    const { cometMcpSources } = useCometMcpServers();
    const getAvailableSourceLabel = useSourceLabel({ cometMcpSources });
    const includePremiumSources = typeof window !== 'undefined' && window.location.pathname.startsWith('/computer');
    const { sources: apiSources, isSourceAllowed: isApiSourceAllowed, getIsSourceConnected: getIsApiSourceConnected, getSource: getApiSource, refetchSources, isSourcesLoading: isApiSourcesLoading, isFetching: isApiSourcesFetching, engineMode, } = useApiSources({
        reason: 'use-sources-hook',
        enabled,
        searchQuery,
        noLimit,
        includePremiumSources,
        // Grouped families are the canonical composer response shape. This is
        // deliberately unconditional (not flag-driven): per-consumer or async
        // flag resolution is what previously split the React Query cache into
        // grouped and ungrouped request variants on the same page.
        groupByFamily: true,
        productSurface,
        // The Ask query surface can only run MCP / native-API sources; Computer
        // threads can also use CLI-only sources (e.g. via @-mention).
        supportedExecEnvs: productSurface === 'ask' ? [...ASK_EXEC_ENVS] : undefined,
    });
    const isOmitted = useCallback((sourceId) => isSourceOmitted(getApiSource(sourceId), omittedSources), [omittedSources, getApiSource]);
    const isCometMcpSourceAllowed = useCallback((sourceType) => {
        const key = getCometMcpServerName(sourceType);
        return Object.keys(cometMcpSources).includes(key);
    }, [cometMcpSources]);
    /**
     * Check if a source is allowed based on the current user's permissions.
     */
    const isSourceAllowed = useMemo(() => createSourceAllowedPredicate({
        isCometMcpSourceAllowed,
        isOmitted,
        omitCometMcpSources,
        isApiSourceAllowed,
    }), [isCometMcpSourceAllowed, isOmitted, omitCometMcpSources, isApiSourceAllowed]);
    /**
     * Check if a source is connected based on the current user's permissions.
     */
    const isSourceConnected = useMemo(() => createSourceConnectedPredicate({
        isCometMcpSourceAllowed,
        isOmitted,
        omitCometMcpSources,
        getIsApiSourceConnected,
        apiSources,
    }), [isCometMcpSourceAllowed, isOmitted, omitCometMcpSources, getIsApiSourceConnected, apiSources]);
    const getSourceLabel = useMemo(() => createSourceLabelGetter({
        getApiSource,
        getAvailableSourceLabel,
    }), [getApiSource, getAvailableSourceLabel]);
    const getSource = useCallback((sourceType) => getApiSource(sourceType), [getApiSource]);
    const sources = useMemo(() => getSourceIds({ apiSources, isSourceAllowed, cometMcpSources, getSourceLabel }), [apiSources, isSourceAllowed, cometMcpSources, getSourceLabel]);
    return useMemo(() => {
        return {
            sources,
            isSourceAllowed,
            isSourceConnected,
            refetchSources,
            getSourceLabel,
            getSource,
            isLoading: isApiSourcesLoading,
            isFetching: isApiSourcesFetching,
            engineMode,
        };
    }, [
        sources,
        isSourceAllowed,
        isSourceConnected,
        refetchSources,
        getSourceLabel,
        getSource,
        isApiSourcesLoading,
        isApiSourcesFetching,
        engineMode,
    ]);
};
//# sourceMappingURL=useSources.js.map